import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { buildWesHubTheme } from '../theme/buildWesHubTheme';
import {
  STORAGE_THEME_KEY,
  getStoredThemePreference,
  resolveEffectiveColorMode,
} from '../styles/designTokens';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function applyDomTheme(mode) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.colorMode = mode;
  root.style.colorScheme = mode === 'dark' ? 'dark' : 'light';
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', mode === 'dark' ? '#000000' : '#EEEFF0');
}

export const ThemeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(() =>
    typeof window !== 'undefined' ? getStoredThemePreference() : 'auto'
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    return Boolean(mq?.matches);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = () => setSystemPrefersDark(Boolean(mq.matches));
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    if (mq.addListener) {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
    return undefined;
  }, []);

  const mode = useMemo(
    () => resolveEffectiveColorMode(preference, systemPrefersDark),
    [preference, systemPrefersDark]
  );

  const setPreference = useCallback((next) => {
    const p =
      next === 'dark' || next === 'light' || next === 'auto' ? next : 'auto';
    setPreferenceState(p);
    try {
      window.localStorage.setItem(STORAGE_THEME_KEY, p);
    } catch {
      /* ignore */
    }
  }, []);

  const setMode = useCallback(
    (next) => {
      setPreference(next === 'dark' ? 'dark' : 'light');
    },
    [setPreference]
  );

  const toggleTheme = useCallback(() => {
    const eff = resolveEffectiveColorMode(preference, systemPrefersDark);
    setPreference(eff === 'dark' ? 'light' : 'dark');
  }, [preference, systemPrefersDark, setPreference]);

  useEffect(() => {
    applyDomTheme(mode);
  }, [mode]);

  const theme = useMemo(() => buildWesHubTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      preference,
      setPreference,
      mode,
      setMode,
      theme,
      toggleTheme,
      isDark: mode === 'dark',
    }),
    [preference, setPreference, mode, setMode, theme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
